import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ComposedChart, Area, LineChart, Line,
} from 'recharts';
import {
  Search, RefreshCw, TrendingUp, Users, Layers, Building2,
  AlertTriangle, DollarSign, CreditCard,
} from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { HelpBubble } from '@/components/HelpBubble';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { useUser } from '@/lib/UserContext';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const COLORS = ['hsl(var(--primary))', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1'];

// fmt function moved inside component to access currency symbol

/**
 * KpiCard with a graceful "no data" state.
 * Pass `hasData={false}` + `missingHint="Column name"` to render the card
 * skeleton with a muted em-dash and a short helper line — avoids empty/0
 * tiles when the user hasn't uploaded the supporting column yet.
 */
const money2 = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CurrencySplit({ rows, total, cs }) {
  if (!rows?.length) return <p className="text-muted-foreground">No currency data</p>;
  return (
    <div className="space-y-2" data-testid="total-spend-currency-split">
      <p className="font-semibold">Total spend by original currency</p>
      <table className="w-full">
        <thead>
          <tr className="text-muted-foreground"><th className="text-left font-normal">Currency</th><th className="text-right font-normal">Original</th><th className="text-right font-normal">In {cs}</th></tr>
        </thead>
        <tbody>
          <>{rows.map(r => (
            <tr key={r.currency} data-testid={`currency-split-${r.currency}`}>
              <td className="py-0.5 font-medium">{r.currency}</td>
              <td className="py-0.5 text-right font-mono">{money2(r.native_amount)}</td>
              <td className="py-0.5 text-right font-mono">{cs}{money2(r.base_amount)} <span className="text-muted-foreground">({r.pct}%)</span></td>
            </tr>
          ))}</>
        </tbody>
      </table>
      <div className="flex justify-between border-t pt-1.5 font-semibold">
        <span>Total</span><span className="font-mono">{cs}{money2(total)}</span>
      </div>
      <p className="text-[10px] text-muted-foreground">Converted at your organisation's exchange rates (Currency page).</p>
    </div>
  );
}

function TrendLinesChart({ testId, title, description, rows, seriesKeys, height, fmt, tooltipStyle, delay }) {
  const keys = seriesKeys || [];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <Card data-testid={testId} className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          {rows?.length > 0 ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={height}>
                <LineChart data={rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={10} angle={-30} textAnchor="end" height={50} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={v => fmt(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmt(v), name]} labelFormatter={l => `Month: ${l}`} />
                  {keys.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} name={k} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3">
                {keys.map((k, i) => (
                  <div key={k} className="flex items-center gap-1.5 text-xs">
                    <div className="w-3 h-0.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-muted-foreground">{k}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No trend data available</p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function OrgBreakdownChart({ testId, title, description, rows, emptyText, fmt, tooltipStyle, delay }) {
  const data = rows || [];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <Card data-testid={testId} className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-500" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          {data.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(220, data.length * 34)}>
              <BarChart data={data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={v => fmt(v)} />
                <YAxis dataKey="name" type="category" width={150} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, item) => [`${fmt(v)} (${item?.payload?.pct ?? 0}%)`, 'Spend']} />
                <Bar dataKey="spend" radius={[0, 6, 6, 0]}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground py-8">{emptyText}</p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, color, delay = 0, testId, hasData = true, missingHint, help, hover }) {
  const empty = !hasData;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <Card
        className={`border-l-4 transition-shadow duration-200 ${empty ? 'opacity-60' : 'hover:shadow-lg'}`}
        style={{ borderLeftColor: empty ? '#cbd5e1' : color }}
        data-testid={testId}
        data-empty={empty ? 'true' : 'false'}
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2 gap-2">
          <CardTitle className="text-xs font-medium text-muted-foreground whitespace-nowrap truncate flex items-center gap-1">
            {title}
            {help ? <HelpBubble text={help} /> : null}
          </CardTitle>
          <Icon className="w-5 h-5 shrink-0" style={{ color: empty ? '#94a3b8' : color }} />
        </CardHeader>
        <CardContent>
          {empty ? (
            <>
              <div className="text-xl font-bold font-mono tracking-tight text-muted-foreground/70" data-testid={testId ? `${testId}-empty` : undefined}>&mdash;</div>
              <p className="text-[11px] text-muted-foreground/80 mt-1 leading-tight">
                {missingHint ? `Upload ${missingHint} to populate` : 'No data available'}
              </p>
            </>
          ) : (
            <>
              {hover ? (
                <HoverCard openDelay={150}>
                  <HoverCardTrigger asChild>
                    <div className="text-xl font-bold font-mono tracking-tight truncate cursor-help" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 text-xs">{hover}</HoverCardContent>
                </HoverCard>
              ) : (
                <div className="text-2xl font-bold font-mono tracking-tight truncate">{value}</div>
              )}
              <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function SpendAnalysis() {
  const { symbol: cs } = useCurrency();
  const user = useUser();
  const { filters, setFilters } = useGlobalFilters();
  
  // Format function using currency symbol
  const fmt = (v) => {
    if (v >= 1e9) return `${cs}${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${cs}${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${cs}${(v / 1e3).toFixed(0)}K`;
    return `${cs}${v.toFixed(0)}`;
  };
  
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deepDive, setDeepDive] = useState(null);

  const buildQueryString = useCallback((filterObj) => {
    const params = new URLSearchParams();
    Object.entries(filterObj).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    return params.toString();
  }, []);

  const fetchData = useCallback(async (filterObj = filters) => {
    try {
      const qs = buildQueryString(filterObj);
      const qp = qs ? `?${qs}` : '';
      const [txRes, ddRes] = await Promise.all([
        api.get(`/transactions/filtered${qp}${qs ? '&' : '?'}limit=1000`),
        api.get(`/analytics/spend-deep-dive${qp}`),
      ]);
      setTransactions(txRes.data.transactions || []);
      setFilteredTransactions(txRes.data.transactions || []);
      setDeepDive(ddRes.data);
    } catch {
      toast.error('Failed to load spend analysis data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQueryString, filters]);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    if (!searchTerm) { setFilteredTransactions(transactions); return; }
    const lc = searchTerm.toLowerCase();
    setFilteredTransactions(transactions.filter(t =>
      t.supplier_name?.toLowerCase().includes(lc) ||
      t.invoice_line_description?.toLowerCase().includes(lc) ||
      t.category_level_1?.toLowerCase().includes(lc)
    ));
  }, [searchTerm, transactions]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setRefreshing(true);
    fetchData(newFilters);
  };

  const handleRefresh = () => { setRefreshing(true); fetchData(filters); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const dd = deepDive || {};
  const activeFiltersCount = Object.values(filters).filter(v => v).length;
  const categoryDepth = dd.category_depth || [];

  // Export helpers
  const exportCSV = () => {
    const rows = [
      ['--- Spend Analysis Summary ---'],
      ['Total Spend', `${cs}${dd.total_spend?.toLocaleString()}`],
      ['Transactions', dd.total_transactions],
      ['Suppliers', dd.total_suppliers],
      ['Avg Transaction', `${cs}${dd.avg_transaction_value?.toLocaleString()}`],
      [], ['--- Supplier Fragmentation ---'],
      ['Single-Source Categories', dd.supplier_fragmentation?.single_source_count],
      ['Multi-Source Categories', dd.supplier_fragmentation?.multi_source_count],
      ['Single-Source Spend %', `${dd.supplier_fragmentation?.single_source_spend_pct}%`],
      ['Avg Suppliers/Category', dd.supplier_fragmentation?.avg_suppliers_per_category],
      [], ['--- Spend by SBU ---'], ['SBU', 'Spend', '%'],
      ...(dd.spend_by_sbu || []).map(r => [r.name, r.spend, `${r.pct}%`]),
      [], ['--- Spend by Department ---'], ['Department', 'Spend', '%'],
      ...(dd.spend_by_business_unit || []).map(r => [r.name, r.spend, `${r.pct}%`]),
      [], ['--- Department Spend Trends ---'], ['Month', ...(dd.department_trend_keys || [])],
      ...(dd.department_trends || []).map(r => [r.month, ...(dd.department_trend_keys || []).map(k => r[k] ?? 0)]),
      [], ['--- Spend by Country ---'], ['Country', 'Spend', '%'],
      ...(dd.spend_by_country || []).map(c => [c.name, c.spend, `${c.pct}%`]),
      [], ['--- Top 10 Transactions ---'], ['Date', 'Supplier', 'Category', 'Amount'],
      ...(dd.top_transactions || []).map(t => [t.date, t.supplier, t.category, t.amount]),
    ];
    downloadCSV(rows, 'spend_analysis');
  };

  const exportPDF = () => {
    buildPDF([
      { title: 'Summary', headers: ['Metric', 'Value'], rows: [
        ['Total Spend', `${cs}${dd.total_spend?.toLocaleString()}`],
        ['Transactions', String(dd.total_transactions)],
        ['Unique Suppliers', String(dd.total_suppliers)],
        ['Avg Transaction', `${cs}${dd.avg_transaction_value?.toLocaleString()}`],
      ]},
      { title: 'Spend by SBU', headers: ['SBU', 'Spend', '%'], rows: (dd.spend_by_sbu || []).map(r => [r.name, `${cs}${r.spend?.toLocaleString()}`, `${r.pct}%`]) },
      { title: 'Spend by Department', headers: ['Department', 'Spend', '%'], rows: (dd.spend_by_business_unit || []).map(r => [r.name, `${cs}${r.spend?.toLocaleString()}`, `${r.pct}%`]) },
      ...((dd.department_trends || []).length ? [{ title: 'Department Spend Trends', headers: ['Month', ...(dd.department_trend_keys || [])], rows: dd.department_trends.map(r => [r.month, ...(dd.department_trend_keys || []).map(k => `${cs}${Number(r[k] || 0).toLocaleString()}`)]) }] : []),
      { title: 'Spend by Country', headers: ['Country', 'Spend', '%'], rows: (dd.spend_by_country || []).map(c => [c.name, `${cs}${c.spend?.toLocaleString()}`, `${c.pct}%`]) },
    ], 'Spend_Analysis');
  };

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  };

  return (
    <div className="space-y-4" data-testid="spend-analysis-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Spend Analysis</h1>
          <p className="text-sm text-muted-foreground">
            Deep dive into spending patterns, supplier concentration & category insights
            {activeFiltersCount > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageName="spend-analysis"
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        showDateRange={true}
        compact={true}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiCard
          title="TOTAL SPEND"
          value={`${cs}${Number(dd.total_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          hover={<CurrencySplit rows={dd.currency_breakdown} total={dd.total_spend} cs={cs} />}
          subtitle={`Avg ${fmt(dd.avg_transaction_value || 0)}/txn`}
          icon={DollarSign} color="#3b82f6" delay={0.1}
          testId="total-spend-card"
          help="Total spend in the current view, FX-normalised. Sub-text shows mean transaction value; a low value across many rows can signal buying fragmentation."
        />
        <KpiCard
          title="TOTAL TRANSACTIONS"
          value={dd.total_transactions?.toLocaleString() || 0}
          subtitle={`Across ${dd.total_suppliers?.toLocaleString() || 0} suppliers`}
          icon={CreditCard} color="#8b5cf6" delay={0.13}
          testId="total-transactions-card"
          help="Row count in the filtered dataset. Compare to Active Suppliers to see how concentrated your buying is."
        />
        <KpiCard
          title="ACTIVE SUPPLIERS"
          value={dd.total_suppliers?.toLocaleString() || 0}
          subtitle={`In ${dd.spend_by_country?.length || 0} countries`}
          icon={Users} color="#06b6d4" delay={0.15}
          testId="active-suppliers-card"
          help="Distinct suppliers with at least one transaction in this view. If this grows faster than spend, tail-spend consolidation could reduce management overhead."
        />
        <KpiCard
          title="ACTIVE CATEGORIES"
          value={categoryDepth.length}
          subtitle={`Avg ${categoryDepth.length ? fmt(dd.total_spend / categoryDepth.length) : cs + '0'}/category`}
          icon={Layers} color="#f59e0b" delay={0.18}
          testId="categories-card"
          help="Number of Level-1 categories with spend. If this is very low, you may be under-classifying — try uploading a category_level_2 column."
        />
        <KpiCard
          title="SINGLE-SOURCE RISK"
          value={`${dd.supplier_fragmentation?.single_source_count || 0} categories`}
          subtitle={`${dd.supplier_fragmentation?.single_source_spend_pct || 0}% of spend at risk`}
          icon={AlertTriangle} color="#ef4444" delay={0.23}
          testId="fragmentation-card"
          hasData={dd.supplier_fragmentation?.single_source_count !== undefined}
          missingHint="category + supplier_name columns"
          help="Categories with only one active supplier. Concentrated supply = negotiation and continuity risk. See Commonality Analysis for dual-source opportunities."
        />
      </div>

      {/* Row 1: Quarterly Spend + Spend by SBU */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card data-testid="quarterly-spend-chart">
            <CardHeader>
              <CardTitle>Quarterly Spend</CardTitle>
              <CardDescription>Spend distribution across fiscal quarters</CardDescription>
            </CardHeader>
            <CardContent>
              {dd.spend_by_quarter?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={dd.spend_by_quarter}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="quarter" stroke="hsl(var(--muted-foreground))" fontSize={11} angle={-30} textAnchor="end" height={50} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={v => fmt(v)} />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), 'Spend']} />
                    <Area type="monotone" dataKey="spend" fill="hsl(var(--primary))" fillOpacity={0.1} stroke="none" />
                    <Bar dataKey="spend" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">No quarterly data available</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <OrgBreakdownChart
          testId="spend-by-sbu-chart"
          title="Spend by SBU"
          description="Strategic business units (divisions) — e.g. Corporate HQ, EMEA Operations"
          rows={dd.spend_by_sbu}
          emptyText="No SBU data available"
          fmt={fmt}
          tooltipStyle={tooltipStyle}
          delay={0.35}
        />
      </div>

      {/* Row 1b: Spend by Department + Department Spend Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <OrgBreakdownChart
          testId="spend-by-department-chart"
          title="Spend by Department"
          description="Functions / departments — e.g. IT, Finance, HR — across all SBUs"
          rows={dd.spend_by_business_unit}
          emptyText="No department data available"
          fmt={fmt}
          tooltipStyle={tooltipStyle}
          delay={0.38}
        />
        <TrendLinesChart
          testId="department-trends-chart"
          title="Department Spend Trends"
          description="Monthly spend trajectory by department"
          rows={dd.department_trends}
          seriesKeys={dd.department_trend_keys}
          height={Math.max(220, (dd.spend_by_business_unit?.length || 0) * 34)}
          fmt={fmt}
          tooltipStyle={tooltipStyle}
          delay={0.4}
        />
      </div>

      {/* Row 2: Category Spend (bar) + Category Trends (line) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card data-testid="category-spend-chart" className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-500" />
                Category Spend
              </CardTitle>
              <CardDescription>Spend distribution by category</CardDescription>
            </CardHeader>
            <CardContent>
              {categoryDepth.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(220, categoryDepth.length * 38)}>
                  <BarChart data={categoryDepth} layout="vertical" margin={{ left: 5, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={v => fmt(v)} />
                    <YAxis dataKey="category" type="category" width={130} stroke="hsl(var(--muted-foreground))" fontSize={11}
                      tickFormatter={v => v.length > 18 ? v.slice(0, 16) + '...' : v} />
                    <Tooltip contentStyle={tooltipStyle}
                      formatter={(v, name, props) => {
                        const pct = dd.total_spend ? (v / dd.total_spend * 100).toFixed(1) : 0;
                        return [`${fmt(v)} (${pct}%)`, 'Spend'];
                      }} />
                    <Bar dataKey="total_spend" radius={[0, 6, 6, 0]} name="Spend">
                      {categoryDepth.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">No category data available</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <TrendLinesChart
          testId="category-trends-chart"
          title="Category Spend Trends"
          description="Monthly spend trajectory for top categories"
          rows={dd.category_trends}
          seriesKeys={dd.category_trend_keys}
          height={Math.max(220, categoryDepth.length * 38)}
          fmt={fmt}
          tooltipStyle={tooltipStyle}
          delay={0.45}
        />
      </div>

      {/* Row 3: Supplier Fragmentation (full width) */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <Card data-testid="fragmentation-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Supplier Fragmentation
            </CardTitle>
            <CardDescription>Number of suppliers per category — identifies consolidation opportunities & single-source risk</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {dd.supplier_fragmentation && (
              <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 text-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-muted-foreground">Single-source:</span>
                  <span className="font-semibold">{dd.supplier_fragmentation.single_source_count}</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="text-muted-foreground">Multi-source:</span>
                  <span className="font-semibold">{dd.supplier_fragmentation.multi_source_count}</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Avg:</span>
                  <span className="font-semibold">{dd.supplier_fragmentation.avg_suppliers_per_category} suppliers/cat</span>
                </div>
              </div>
            )}
            {categoryDepth.length > 0 && (
              <ResponsiveContainer width="100%" height={Math.max(200, categoryDepth.length * 36)}>
                <BarChart data={categoryDepth} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <YAxis dataKey="category" type="category" width={130} stroke="hsl(var(--muted-foreground))" fontSize={11}
                    tickFormatter={v => v.length > 18 ? v.slice(0, 16) + '...' : v} />
                  <Tooltip contentStyle={tooltipStyle}
                    formatter={(v, name, props) => {
                      const cat = props.payload;
                      return [
                        <span key="v">{v} supplier{v !== 1 ? 's' : ''} — {cat.supplier_count === 1 ? <span style={{color:'#ef4444'}}>Single-source risk</span> : <span style={{color:'#10b981'}}>Diversified</span>}</span>,
                        'Suppliers'
                      ];
                    }}
                  />
                  <Bar dataKey="supplier_count" radius={[0, 6, 6, 0]} name="Suppliers">
                    {categoryDepth.map((cat) => (
                      <Cell key={cat.category} fill={cat.supplier_count === 1 ? '#ef4444' : cat.supplier_count <= 5 ? '#f59e0b' : '#10b981'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Top 10 Largest Transactions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
        <Card data-testid="top-transactions-table">
          <CardHeader>
            <CardTitle>Top 10 Largest Transactions</CardTitle>
            <CardDescription>Highest-value individual transactions for review</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-center py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-8">#</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Supplier</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                    <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <>{(dd.top_transactions || []).map((t, i) => (
                    <tr key={`${t.supplier || ''}-${t.date || ''}-${t.amount ?? ''}-${i}`} className="border-b border-border hover:bg-muted/50 transition-colors" data-testid="top-txn-row">
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${i < 3 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          {i + 1}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-sm font-medium">{t.supplier}</td>
                      <td className="py-1.5 px-3 text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">{t.category}</span>
                      </td>
                      <td className="py-1.5 px-3 text-sm text-muted-foreground max-w-[200px] truncate">{t.description}</td>
                      <td className="py-1.5 px-3 text-sm text-muted-foreground">{t.date || 'N/A'}</td>
                      <td className="py-1.5 px-3 text-sm font-mono font-semibold text-right">{cs}{t.amount?.toLocaleString()}</td>
                    </tr>
                  ))}</>
                </tbody>
              </table>
              {(!dd.top_transactions || dd.top_transactions.length === 0) && (
                <p className="text-center text-muted-foreground py-8">No transaction data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Transaction Search */}
      <Card data-testid="transactions-section">
        <CardContent className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search transactions by supplier, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          {searchTerm && (
            <p className="text-xs text-muted-foreground mt-2">
              {filteredTransactions.length.toLocaleString()} result{filteredTransactions.length !== 1 ? 's' : ''} found
            </p>
          )}
        </CardContent>
      </Card>

      {/* AI Insights Panel — moved to bottom as requested */}
      <AIPanel page="spend-analysis" filters={filters} />
    </div>
  );
}
